import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AOS from 'aos';
import 'aos/dist/aos.css';
import CustomIframe from '../UIKit/CustomIframe';
import VideoContent from '../UIKit/VideoContent';
import Labyrinth from '../UIKit/Labyrinth';
import FlippingCards from '../UIKit/FlippingCards';
import MemoryGame from '../MemoryGame';
import TagCloud from '../TagCloud';
import RolePlayGame from '../RolePlayGame';
import { useTranslation } from 'react-i18next';
import { htmlContentStyles } from '../../utils/stylesUtils';

const LessonContentBlocks = ({ lesson, contentBlocks }) => {
  const { t } = useTranslation(['lessons']);

  // Initialize AOS for animations in HTML content
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
    });
  }, []);

  // Refresh AOS when content changes
  useEffect(() => {
    // Небольшая задержка для корректного обновления после рендеринга
    const timer = setTimeout(() => {
      AOS.refresh();
    }, 100);

    return () => clearTimeout(timer);
  }, [lesson, contentBlocks]);

  // Объединяем все блоки и сортируем по order
  const allBlocks = React.useMemo(() => {
    const blocks = [];

    // Если contentType не "mixed", то контент хранится в поле content как single-block
    if (lesson && lesson.contentType && lesson.contentType !== 'mixed') {
      // Создаем блок из поля content в зависимости от contentType
      if (lesson.content) {
        blocks.push({
          content: lesson.content,
          type: lesson.contentType,
          blockType: 'content',
          order: 0,
          _id: 'single-block-content',
        });
      }
    } else {
      // Если contentType === "mixed", используем contentBlocks
      if (contentBlocks && contentBlocks.length > 0) {
        contentBlocks.forEach(block => {
          blocks.push({
            ...block,
            blockType: 'content',
            order: block.order || 0,
          });
        });
      }
    }

    // Сортируем по order
    return blocks.sort((a, b) => a.order - b.order);
  }, [lesson, contentBlocks]);

  if (allBlocks.length === 0) return null;

  // Рендерим текстовый блок
  const renderTextBlock = (block) => {
    // Проверка на пустой или отсутствующий контент
    if (!block.content || typeof block.content !== 'string' || block.content.trim() === '') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('lessons:lessonDetail.content.noContent')}
        </Typography>
      );
    }

    try {
      switch (block.type) {
        case 'text':
          const sanitizedText = DOMPurify.sanitize(block.content);

          // Проверка, что после санитизации что-то осталось
          if (!sanitizedText || sanitizedText.trim() === '') {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('lessons:lessonDetail.content.invalidHtml')}
              </Typography>
            );
          }

          return (
            <Typography
              variant="body1"
              component="div"
              sx={{
                mb: 2,
                ...htmlContentStyles,
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizedText,
              }}
            />
          );

        case 'presentation':
        case 'htmlBlock':
          const sanitizedHtml = DOMPurify.sanitize(block.content);

          if (!sanitizedHtml || sanitizedHtml.trim() === '') {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('lessons:lessonDetail.content.invalidHtml')}
              </Typography>
            );
          }

          return (
            <Box
              sx={{
                mb: 2,
                overflow: 'hidden',
                ...htmlContentStyles,
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizedHtml,
              }}
            />
          );

        case 'fullHtml':
          return (
            <CustomIframe
              srcDoc={block.content}
              title="Full HTML Content Block"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
              style={{ minHeight: '600px' }}
            />
          );

        case 'standard':
          return (
            <Box
              sx={{
                '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: 'background.default',
                  fontWeight: 'bold',
                },
                '& img': {
                  width: '100%',
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {block.content}
              </ReactMarkdown>
            </Box>
          );

        default:
          return null;
      }
    } catch (error) {
      console.error('Помилка при обробці HTML контенту:', error);
      return (
        <Typography color="error" variant="body2">
          {t('lessons:lessonDetail.content.htmlRenderError')}
        </Typography>
      );
    }
  };

  // Рендерим изображение
  const renderImageBlock = (block) => {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <img
          src={block.url}
          alt={block.alt || t('lessons:lessonDetail.content.imageAlt')}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        {block.caption && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            {block.caption}
          </Typography>
        )}
      </Box>
    );
  };

  // Рендерим интерактивный блок
  const renderInteractiveBlock = (block) => {
    switch (block.type) {
      case 'video':
        try {
          const videoData = JSON.parse(block.content);
          return <VideoContent options={videoData} />;
        } catch (e) {
          console.error('Ошибка при обработке видео контента:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.videoError')}
            </Typography>
          );
        }

      case 'labyrinth':
        try {
          // Validate that the content is valid JSON before passing it
          JSON.parse(block.content);
          return (
            <Labyrinth
              content={block.content}
              onChange={(result) => {
                console.log('Labyrinth result:', result);
              }}
            />
          );
        } catch (e) {
          console.error('Ошибка при обработке лабиринта:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.labyrinthError')}
            </Typography>
          );
        }

      case 'flippingCards':
        try {
          // Validate JSON before passing
          JSON.parse(block.content);
          return (
            <FlippingCards
              content={block.content}
              onChange={(result) => {
                console.log('FlippingCards result:', result);
              }}
            />
          );
        } catch (e) {
          console.error('Ошибка при обработке карточек:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.cardsError')}
            </Typography>
          );
        }

      case 'memoryGame':
        try {
          const memoryData = JSON.parse(block.content);
          return (
            <MemoryGame
              options={memoryData}
              onChange={(result) => {
                console.log('MemoryGame result:', result);
              }}
            />
          );
        } catch (e) {
          console.error('Ошибка при обработке игры памяти:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.memoryError')}
            </Typography>
          );
        }

      case 'tagCloud':
        try {
          const tagCloudData = JSON.parse(block.content);
          return (
            <TagCloud
              options={tagCloudData}
              onChange={(result) => {
                console.log('TagCloud result:', result);
              }}
            />
          );
        } catch (e) {
          console.error('Ошибка при обработке облака тегов:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.tagCloudError')}
            </Typography>
          );
        }

      case 'rolePlayGame':
        try {
          const rolePlayData = JSON.parse(block.content);
          return (
            <RolePlayGame
              options={rolePlayData}
              onChange={(result) => {
                console.log('RolePlayGame result:', result);
              }}
            />
          );
        } catch (e) {
          console.error('Ошибка при обработке ролевой игры:', e);
          return (
            <Typography color="error">
              {t('lessons:lessonDetail.content.rolePlayError')}
            </Typography>
          );
        }

      default:
        return null;
    }
  };

  // Рендерим блок в зависимости от типа
  const renderBlock = (block) => {
    // Текстовые блоки
    if (['text', 'standard', 'presentation', 'htmlBlock', 'fullHtml'].includes(block.type)) {
      return renderTextBlock(block);
    }

    // Изображения
    if (block.type === 'image') {
      return renderImageBlock(block);
    }

    // Интерактивные блоки
    if (['video', 'labyrinth', 'flippingCards', 'memoryGame', 'tagCloud', 'rolePlayGame'].includes(block.type)) {
      return renderInteractiveBlock(block);
    }

    return null;
  };

  return (
    <>
      {allBlocks.map((block, index) => {
        const content = renderBlock(block);
        if (!content) return null;

        return (
          <Box
            key={block._id || `block-${index}`}
            sx={{
              mb: 4,
              borderRadius: '12px',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              p: 3,
            }}
          >
            {content}
          </Box>
        );
      })}
    </>
  );
};

export default LessonContentBlocks;
